import { pickT } from "@repo/shared";
import type { PdfSectionScope } from "@repo/shared/types";
import { useMemo, useRef } from "react";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import {
  buildFieldMap,
  flowFieldDisplayRef,
  transformToDisplay,
  transformToStorage,
} from "@/lib/services/placeholder-transformer";
import { AdvisorNoteMenuItems } from "./advisor-note-menu-items";
import { T } from "./heading-form.i18n";
import { type ComponentFormProps, NO_ADVISOR_NOTE_PAGES } from "./types";

export type HeadingConfig = {
  title: string;
  color?: string; // Hex color (e.g. "#000000"); undefined = theme default
};

type HeadingFormProps = ComponentFormProps<HeadingConfig> & {
  sectionScope?: PdfSectionScope;
};

export function HeadingForm({
  config,
  onChange,
  flowFields,
  // QL-13 — `{{Quiz.<name>}}` renders the client's result for that quiz.
  quizzes: activeQuizzes = [],
  advisorNotePages = NO_ADVISOR_NOTE_PAGES,
  clientCustomFields = [],
  sectionScope,
}: HeadingFormProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build field map for placeholder transformation
  const fieldMap = useMemo(
    () =>
      buildFieldMap(clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages),
    [clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages],
  );

  // Transform content from storage (IDs) to display (localized names)
  const displayTitle = useMemo(
    () => transformToDisplay(config.title || "", fieldMap),
    [config.title, fieldMap],
  );

  // Handle title change: transform from display to storage format
  const handleTitleChange = (newDisplayTitle: string) => {
    const storageTitle = transformToStorage(newDisplayTitle, fieldMap);
    onChange({ ...config, title: storageTitle });
  };

  // Insert a field placeholder (display value)
  const insertField = (displayValue: string) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentDisplayTitle = displayTitle;

    const newDisplayTitle = `${currentDisplayTitle.substring(0, start)}{{${displayValue}}}${currentDisplayTitle.substring(end)}`;

    handleTitleChange(newDisplayTitle);

    // Set cursor position after inserted text
    requestAnimationFrame(() => {
      input.focus();
      const newPos = start + displayValue.length + 4; // +4 for {{ and }}
      input.setSelectionRange(newPos, newPos);
    });
  };

  // Client fields (always available)
  const clientFields = [
    { label: t.name, value: "Client.Name" },
    { label: t.type, value: "Client.Type" },
    { label: t.email, value: "Client.Email" },
    { label: t.mobilePhone, value: "Client.MobilePhone" },
    { label: t.ssn, value: "Client.SSN" },
    { label: t.orgId, value: "Client.OrgId" },
    { label: t.country, value: "Client.Country" },
    { label: t.groupMembers, value: "Client.Members" },
    { label: t.representative, value: "Client.Representative" },
  ];

  // Owner fields (advisor / client owner data)
  const ownerFields = [
    { label: t.name, value: "Owner.Name" },
    { label: t.email, value: "Owner.Email" },
    { label: t.phoneNumber, value: "Owner.Phone" },
    { label: t.title, value: "Owner.Title" },
    { label: t.signature, value: "Owner.Signature" },
  ];

  // Flow fields (flow instance data)
  const flowInstanceFields = [
    { label: t.name, value: "Flow.Name" },
    { label: t.cancelReason, value: "Flow.CancelReason" },
    { label: t.expectingChanges, value: "Flow.ExpectingFinancialChanges" },
    { label: t.expectingChangesNote, value: "Flow.ExpectingFinancialChangesNote" },
  ];

  // Document fields (always available)
  const documentFields = [{ label: t.today, value: "today" }];

  // Goal fields (only available in per_goal scope)
  const goalFields = [
    { label: t.goalName, value: "Goal Name" },
    { label: t.goalType, value: "Goal Type" },
    { label: t.goalDescription, value: "Goal Description" },
    { label: t.riskScore, value: "Risk Score" },
    // QL-375 group 10 — the goal's mandate (empty on a goal without one)
    { label: t.mandateName, value: "Mandate Name" },
    { label: t.mandateStatus, value: "Mandate Status" },
    { label: t.mandateSource, value: "Mandate Source" },
    { label: t.mandateOrigin, value: "Mandate Origin" },
    { label: t.mandateMode, value: "Mandate Mode" },
    { label: t.existingMandateName, value: "Existing Mandate Name" },
    { label: t.mandateDeviationNote, value: "Mandate Deviation Note" },
    { label: t.mandateDecidedBy, value: "Mandate Decided By" },
    { label: t.mandateDecidedAt, value: "Mandate Decided At" },
  ];
  // Per-portfolio sections only: QL-375 group 10b mandate verdict + QL-72 model deviation
  const portfolioSectionFields = [
    { label: t.portfolioMandateStatus, value: "Portfolio Mandate Status" },
    { label: t.portfolioMandateDeviationNote, value: "Portfolio Mandate Deviation Note" },
    { label: t.portfolioMandateDecidedBy, value: "Portfolio Mandate Decided By" },
    { label: t.portfolioMandateDecidedAt, value: "Portfolio Mandate Decided At" },
    // QL-72 — model portfolio picked from another risk score
    { label: t.portfolioModelDeviation, value: "Portfolio Model Deviation" },
    { label: t.portfolioModelRiskScore, value: "Portfolio Model Risk Score" },
    { label: t.portfolioGoalRiskScore, value: "Portfolio Goal Risk Score" },
  ];
  // QL-414 — the portfolio's own name, where a section prints per portfolio.
  if (sectionScope === "per_portfolio") {
    goalFields.push({ label: t.portfolioName, value: "Portfolio Name" });
  }

  // QP-150: per_portfolio sections also carry the parent goal's context, so goal fields apply.
  const isPerGoalScope = sectionScope === "per_goal" || sectionScope === "per_portfolio";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="heading-title">
          {t.headingTitle} <span className="text-destructive">*</span>
        </Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              {t.insertField}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 max-h-[400px] overflow-y-auto">
            <DropdownMenuLabel>{t.clientFields}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {clientFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            {(() => {
              const customOnly = clientCustomFields.filter((f) => f.isActive && !f.isStandard);
              return customOnly.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t.customFields}</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {customOnly.map((field) => {
                      // Insert display name - it will be converted to ID on save
                      const displayName = localize(field.nameI18n) || field.name;
                      return (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => insertField(`Client.${displayName}`)}
                        >
                          {displayName}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </>
              ) : null;
            })()}

            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.ownerFields}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {ownerFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            {isPerGoalScope && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.goalFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {goalFields.map((field) => (
                    <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}

            {sectionScope === "per_portfolio" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.portfolioFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {portfolioSectionFields.map((field) => (
                    <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}

            {(() => {
              // Filter flow fields by scope:
              // - In global sections: only show global-scoped fields
              // - In per_goal sections: show all fields
              const filteredFlowFields = flowFields.filter((field) => {
                if (field.scope === "portfolio" && sectionScope !== "per_portfolio") return false;
                if (field.scope === "goal" && sectionScope === "global") return false;
                return true;
              });

              return filteredFlowFields.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t.flowFields}</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {filteredFlowFields.map((field) => {
                      // Insert display name - it will be converted to ID on save
                      // (by id when another field shares the name, QL-610)
                      const displayName = field.name;
                      return (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => insertField(flowFieldDisplayRef(field, fieldMap))}
                        >
                          {displayName}
                          {field.scope === "goal" && (
                            <span className="ml-2 text-xs text-muted-foreground">[{t.goal}]</span>
                          )}
                          {field.scope === "portfolio" && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              [{t.portfolio}]
                            </span>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </>
              ) : null;
            })()}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.flow}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {flowInstanceFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <AdvisorNoteMenuItems
              pages={advisorNotePages}
              sectionScope={sectionScope}
              onInsert={(_label, field) => insertField(field)}
            />
            {activeQuizzes.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.quizFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => insertField(`Quiz.${quiz.name}`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuLabel>{t.quizOverrideFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => insertField(`Quiz.${quiz.name}.Override`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuLabel>{t.quizExternalFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => insertField(`Quiz.${quiz.name}.External`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.document}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {documentFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Input
        ref={inputRef}
        id="heading-title"
        value={displayTitle}
        onChange={(e) => handleTitleChange(e.target.value)}
        placeholder={t.placeholder}
        required
      />
      <p className="text-xs text-muted-foreground">{t.autoNumbered}</p>
      {sectionScope === "per_goal" && (
        <p className="text-xs text-muted-foreground">{t.nestingGoal}</p>
      )}
      {sectionScope === "per_portfolio" && (
        <p className="text-xs text-muted-foreground">{t.nestingPortfolio}</p>
      )}

      <div className="space-y-2 pt-2">
        <Label>{t.textColor}</Label>
        <div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-8 w-32 justify-start gap-2 px-2 font-mono text-xs"
              >
                <span
                  className="h-5 w-5 rounded border border-input"
                  style={{
                    backgroundColor: config.color || "transparent",
                    backgroundImage: config.color
                      ? undefined
                      : "linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)",
                    backgroundSize: config.color ? undefined : "8px 8px",
                    backgroundPosition: config.color ? undefined : "0 0, 4px 4px",
                  }}
                />
                <span className="truncate">{config.color || t.default}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start">
              <HexColorPicker
                color={config.color || "#000000"}
                onChange={(color) => onChange({ ...config, color })}
              />
              <Input
                type="text"
                value={config.color || ""}
                onChange={(e) => onChange({ ...config, color: e.target.value || undefined })}
                placeholder="#000000"
                className="mt-2 w-full font-mono text-sm"
              />
              {config.color && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => onChange({ ...config, color: undefined })}
                >
                  {t.reset}
                </Button>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}

export function validateHeadingConfig(config: HeadingConfig, lang = "en_GB"): string | null {
  const t = pickT(T, lang);
  if (!config.title?.trim()) {
    return t.validationError;
  }
  return null;
}

/**
 * QL-414 — the first heading of a per-goal or per-portfolio section is that
 * goal's or portfolio's entry in the table of contents, so it starts out
 * naming it; a bare "Goal" repeated once per goal tells the reader nothing.
 * Any later heading starts empty.
 */
export function getDefaultHeadingConfig(
  sectionScope?: PdfSectionScope,
  isFirstHeading = false,
): HeadingConfig {
  if (isFirstHeading && sectionScope === "per_goal") return { title: "Goal: {{Goal Name}}" };
  if (isFirstHeading && sectionScope === "per_portfolio") {
    return { title: "Portfolio: {{Portfolio Name}}" };
  }
  return {
    title: "",
  };
}
